import { useEffect, useId, useRef, type ReactNode } from "react";

/**
 * Bottom sheet. Uses a native <dialog> so focus trapping, Escape and the top
 * layer come for free instead of being reimplemented badly.
 *
 * The design gives it no close button, only the grab handle — so the handle is
 * the close button. It looks like the pill in the mockup and behaves like a
 * dismiss for anyone who cannot swipe or reach Escape.
 */
export function Sheet({
  open,
  onClose,
  title,
  subtitle,
  titleHidden = false,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  /** Keep the title for assistive tech but let the body draw its own. */
  titleHidden?: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const heading = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      dialog.showModal();
      // showModal() otherwise lands on the first focusable child — the grab
      // handle — and arms it with a focus ring the moment the sheet appears.
      // The heading is the conventional target: screen readers announce it and
      // nothing is pre-selected.
      heading.current?.focus();
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  // Lock the page behind the sheet so iOS does not scroll it under the keyboard.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClose={onClose}
      onClick={(event) => {
        // Clicking the backdrop (outside the panel) dismisses.
        if (event.target === ref.current) onClose();
      }}
      className="m-0 mt-auto w-full max-w-lg rounded-t-[24px] bg-white p-0 text-text shadow-lg backdrop:bg-[rgba(8,8,10,0.35)] sm:mx-auto sm:mb-auto sm:mt-[12vh] sm:rounded-[24px]"
    >
      <div className="flex flex-col gap-4 px-5 pt-1 pb-[max(28px,env(safe-area-inset-bottom))]">
        {/* The visible pill is 36x4, but the button around it is a real
            44px-tall touch target. */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="group mx-auto -mb-2 flex h-11 w-16 shrink-0 items-center justify-center"
        >
          <span className="h-1 w-9 rounded-full bg-neutral-400 transition-colors group-hover:bg-neutral-500" />
        </button>

        <div
          ref={heading}
          tabIndex={-1}
          className={titleHidden ? "sr-only" : "outline-none"}
        >
          <h2 id={titleId} className="text-xl tracking-[-0.02em]">
            {title}
          </h2>
          {subtitle && <p className="text-xs text-neutral-500">{subtitle}</p>}
        </div>

        {children}
      </div>
    </dialog>
  );
}
